import { SQLFilters } from '../../../../api/types';
import { RegularMetricId } from '../../../../api/types-graphql';
import {
  ChartGridConfig,
  LanguageString,
} from '../../../../common/components/dashboards/types';
import {
  KpiFactboxBenchmarkPeriods,
  KpiFactboxBenchmarkTypes,
} from '../../../../common/components/data-viz-containers/common/types';
import { KpiFactboxSingleMetricVizViews } from '../../../../common/components/data-viz-containers/kpi-factbox-single-metric/types';
import { KpiFactboxTwoMetricVizViews } from '../../../../common/components/data-viz-containers/kpi-factbox-two-metric/types';
import { OverTimeUnsegmentedVizViews } from '../../../../common/components/data-viz-containers/over-time-unsegmented/types';
import { TimePeriodUnsegmentedVizViews } from '../../../../common/components/data-viz-containers/time-period-unsegmented/types';
import {
  StoryboardComponentIds,
  StoryboardItemConfig,
} from '../../../../common/components/storyboard-components/common/types';
import { FactboxFontSizes } from '../../../../common/components/ui-components/data-viz-ui/factboxes/common/types';
import { Languages } from '../../../../constants';
import { DataFieldWithDataType, TimeSelectionType } from '../../../../types';
import { getKeyFromDataFieldWithDataType } from '../../../../utils';
import { StoryboardDateUtils } from '../../utils';
import { getFactboxGridConfig, topLineChartGridConfig } from '../chart-layouts';

export const getHeadcountsTopChartConfig = (
  storyboardDateUtils: StoryboardDateUtils,
  filters: SQLFilters | null
): StoryboardItemConfig => {
  const { twelveMonthsAgoFromNowEndOfMonth, nowEndOfMonth } =
    storyboardDateUtils;
  return {
    chartId: 'top-chart',
    layoutConfig: {
      ...topLineChartGridConfig,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.OVERTIME_UNSEGMENTED,
      dataConfig: {
        title: {
          [Languages.EN]: 'Individuals Historical',
          [Languages.JA]: 'Individuals Historical',
        },
        queries: [
          {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0001_1IndividualsCount,
            },
            filters: filters ?? undefined,
          },
        ],
        timeSelection: {
          type: TimeSelectionType.CalendarYearMonthly,
          input: {
            start: twelveMonthsAgoFromNowEndOfMonth,
            end: nowEndOfMonth,
          },
        },
      },
      selectedView: OverTimeUnsegmentedVizViews.LINE_CHART,
    },
  };
};

export const getHeadcountsByDimensionChartConfig = (
  storyboardDateUtils: StoryboardDateUtils,
  dimension: DataFieldWithDataType,
  title: LanguageString,
  filters: SQLFilters | null,
  gridConfig: ChartGridConfig
): StoryboardItemConfig => {
  const { nowEndOfMonth } = storyboardDateUtils;
  return {
    chartId: `by-dimension-chart-${getKeyFromDataFieldWithDataType(dimension)}`,
    layoutConfig: {
      ...gridConfig,
    },
    componentConfig: {
      componentId: StoryboardComponentIds.TIME_PERIOD_UNSEGMENTED,
      dataConfig: {
        title: title,
        queries: [
          {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0001_1IndividualsCount,
            },
          },
        ],
        filters: filters ?? undefined,
        timeSelection: {
          type: TimeSelectionType.CalendarYearSingleValueByMonths,
          input: {
            start: nowEndOfMonth,
            end: nowEndOfMonth,
          },
        },
        groupBy: {
          type: 'non-hierarchical',
          dataField: dimension,
        },
      },
      selectedView: TimePeriodUnsegmentedVizViews.BAR_CHART,
    },
  };
};

export const getNikkiHeadcountFactbox = (
  storyboardDateUtils: StoryboardDateUtils,
  filters: SQLFilters | null
): StoryboardItemConfig => {
  const { nowEndOfMonth } = storyboardDateUtils;
  return {
    chartId: 'headcount-factbox',
    layoutConfig: {
      ...getFactboxGridConfig(1),
    },
    componentConfig: {
      componentId: StoryboardComponentIds.FACTBOX_SINGLE_METRIC,
      dataConfig: {
        title: {
          [Languages.EN]: 'Headcount',
          [Languages.JA]: 'Headcount',
        },
        fontSize: FactboxFontSizes.SMALL,
        section: {
          query: {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0001_1IndividualsCount,
            },
            timeSelection: {
              type: TimeSelectionType.CalendarYearSingleValueByMonths,
              input: {
                start: nowEndOfMonth,
                end: nowEndOfMonth,
              },
            },
            filters: filters ?? undefined,
          },
          benchmarkConfigs: [
            {
              benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
              benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
            },
          ],
        },
      },
      selectedView: KpiFactboxSingleMetricVizViews.FACTBOX,
    },
  };
};

export const getNikkiTenureFactbox = (
  storyboardDateUtils: StoryboardDateUtils,
  filters: SQLFilters | null
): StoryboardItemConfig => {
  const { nowEndOfMonth } = storyboardDateUtils;
  return {
    chartId: 'tenure-factbox',
    layoutConfig: {
      ...getFactboxGridConfig(2),
    },
    componentConfig: {
      componentId: StoryboardComponentIds.FACTBOX_SINGLE_METRIC,
      dataConfig: {
        title: {
          [Languages.EN]: 'Average Tenure',
          [Languages.JA]: 'Average Tenure',
        },
        fontSize: FactboxFontSizes.SMALL,
        section: {
          query: {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0023_2TenureAvg,
            },
            timeSelection: {
              type: TimeSelectionType.CalendarYearSingleValueByMonths,
              input: {
                start: nowEndOfMonth,
                end: nowEndOfMonth,
              },
            },
            filters: filters ?? undefined,
          },
          benchmarkConfigs: [
            {
              benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
              benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
            },
          ],
        },
      },
      selectedView: KpiFactboxSingleMetricVizViews.FACTBOX,
    },
  };
};

export const getNikkiGenderRatioFactbox = (
  storyboardDateUtils: StoryboardDateUtils,
  filters: SQLFilters | null
): StoryboardItemConfig => {
  const { nowEndOfMonth } = storyboardDateUtils;
  return {
    chartId: 'gender-ratio-factbox',
    layoutConfig: {
      ...getFactboxGridConfig(3),
    },
    componentConfig: {
      componentId: StoryboardComponentIds.FACTBOX_TWO_METRIC,
      dataConfig: {
        title: {
          [Languages.EN]: 'Gender Ratio',
          [Languages.JA]: 'Gender Ratio',
        },
        fontSize: FactboxFontSizes.SMALL,
        sections: {
          leftSection: {
            title: {
              [Languages.EN]: 'Female',
              [Languages.JA]: 'Female',
            },
            query: {
              metric: {
                type: 'RegularMetricId',
                value: RegularMetricId.Pm0020_2FhcPercHc,
              },
              timeSelection: {
                type: TimeSelectionType.CalendarYearSingleValueByMonths,
                input: {
                  start: nowEndOfMonth,
                  end: nowEndOfMonth,
                },
              },
              filters: filters ?? undefined,
            },
            benchmarkConfigs: [
              {
                benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
                benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
              },
            ],
          },
          rightSection: {
            title: {
              [Languages.EN]: 'Male',
              [Languages.JA]: 'Male',
            },
            query: {
              metric: {
                type: 'RegularMetricId',
                value: RegularMetricId.Pm0448_2MhcPerPerson,
              },
              timeSelection: {
                type: TimeSelectionType.CalendarYearSingleValueByMonths,
                input: {
                  start: nowEndOfMonth,
                  end: nowEndOfMonth,
                },
              },
              filters: filters ?? undefined,
            },
            benchmarkConfigs: [
              {
                benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
                benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
              },
            ],
          },
        },
      },
      selectedView: KpiFactboxTwoMetricVizViews.FACTBOX,
    },
  };
};

export const getNikkiAgeFactbox = (
  storyboardDateUtils: StoryboardDateUtils,
  filters: SQLFilters | null
): StoryboardItemConfig => {
  const { nowEndOfMonth } = storyboardDateUtils;
  return {
    chartId: 'age-factbox',
    layoutConfig: {
      ...getFactboxGridConfig(4),
    },
    componentConfig: {
      componentId: StoryboardComponentIds.FACTBOX_SINGLE_METRIC,
      dataConfig: {
        title: {
          [Languages.EN]: 'Average Age',
          [Languages.JA]: 'Average Age',
        },
        fontSize: FactboxFontSizes.SMALL,
        section: {
          query: {
            metric: {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0019_2AgeAvg,
            },
            timeSelection: {
              type: TimeSelectionType.CalendarYearSingleValueByMonths,
              input: {
                start: nowEndOfMonth,
                end: nowEndOfMonth,
              },
            },
            filters: filters ?? undefined,
          },
          benchmarkConfigs: [
            {
              benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
              benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
            },
          ],
        },
      },
      selectedView: KpiFactboxSingleMetricVizViews.FACTBOX,
    },
  };
};
